/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, LogOut, Eye, EyeOff } from 'lucide-react';
import { useSupportSessionStore } from '@/stores/supportSessionStore';
import { useExitSupportSession } from '@/hooks/api/useSuperAdminSupportAccess';

export function SupportSessionBanner() {
  const { t } = useTranslation('common');
  const { activeSession, exitSession } = useSupportSessionStore();
  const exitMutation = useExitSupportSession();
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!activeSession) return;

    const updateCountdown = () => {
      const diff = new Date(activeSession.expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        exitSession();
        return;
      }
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeSession, exitSession]);

  if (!activeSession) return null;

  const handleExit = () => {
    exitMutation.mutate(activeSession.grantId, {
      onSettled: () => {
        exitSession();
      },
    });
  };

  const isReadOnly = activeSession.accessLevel === 'READ_ONLY';

  return (
    <>
      <div className={`ssb-banner ${isReadOnly ? 'ssb-banner--readonly' : 'ssb-banner--fullaccess'}`}>
        <div className="ssb-content">
          <Shield size={16} />
          <span className="ssb-label">{t('supportAccess.banner.label')}</span>
          <span className="ssb-org">{activeSession.orgName}</span>
          <span className="ssb-separator">|</span>
          <span className="ssb-level">
            {isReadOnly ? <Eye size={13} /> : <EyeOff size={13} />}
            {isReadOnly ? t('supportAccess.readOnly') : t('supportAccess.fullAccess')}
          </span>
          <span className="ssb-separator">|</span>
          <span className="ssb-timer">{timeRemaining}</span>
          <button className="ssb-exit" onClick={handleExit} disabled={exitMutation.isPending}>
            <LogOut size={14} />
            {t('supportAccess.banner.exit')}
          </button>
        </div>
      </div>
      <style>{bannerStyles}</style>
    </>
  );
}

const bannerStyles = `
  .ssb-banner {
    position: sticky;
    top: 0;
    z-index: 150;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
  }

  .ssb-banner--readonly {
    background: #fbbf24;
    color: #78350f;
  }

  .ssb-banner--fullaccess {
    background: #ef4444;
    color: white;
  }

  .ssb-content {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 1200px;
    margin: 0 auto;
    flex-wrap: wrap;
  }

  .ssb-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .ssb-org {
    font-weight: 600;
  }

  .ssb-separator {
    opacity: 0.5;
  }

  .ssb-level {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .ssb-timer {
    font-family: var(--font-mono, monospace);
  }

  .ssb-exit {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    padding: 4px 12px;
    border: 1px solid currentColor;
    border-radius: 4px;
    background: transparent;
    color: inherit;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0.9;
  }

  .ssb-exit:hover:not(:disabled) {
    opacity: 1;
    background: rgba(255,255,255,0.15);
  }

  .ssb-exit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
